// Subject Management Component for Admin Dashboard
import React, { useState, useEffect } from 'react';
import ConfirmDialog from './confirmdialog';
import { API_BASE } from '../services/api.js';

const SubjectManagement = ({ onBack }) => {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteSubjectId, setDeleteSubjectId] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    loadSubjects();
  }, []);

  const loadSubjects = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/subjects/`);
      const data = await response.json();
      setSubjects(data);
      setLoading(false);
    } catch (error) {
      setError('Failed to load subjects');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const url = editingSubject 
        ? `${API_BASE}/subjects/${editingSubject.subject_id}`
        : `${API_BASE}/subjects/`;
      
      const method = editingSubject ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSuccess(editingSubject ? 'Subject updated successfully' : 'Subject created successfully');
        setFormData({ name: '', description: '' });
        setShowAddForm(false);
        setEditingSubject(null);
        loadSubjects();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Operation failed');
      }
    } catch (error) {
      setError('Network error');
    }
  };

  const handleEdit = (subject) => {
    setEditingSubject(subject);
    setFormData({
      name: subject.name,
      description: subject.description || ''
    });
    setShowAddForm(true);
  };

  const handleDelete = async (subjectId) => {
    setDeleteSubjectId(subjectId);
    setShowConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await fetch(`${API_BASE}/subjects/${deleteSubjectId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setSuccess('Subject deleted successfully');
        loadSubjects();
      } else {
        setError('Failed to delete subject. It may be used by chapters or students.');
      }
    } catch (error) {
      setError('Network error');
    } finally {
      setShowConfirm(false);
      setDeleteSubjectId(null);
    }
  };

  const cancelDelete = () => {
    setShowConfirm(false);
    setDeleteSubjectId(null);
  };

  const cancelForm = () => {
    setShowAddForm(false);
    setEditingSubject(null);
    setFormData({ name: '', description: '' });
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Loading subjects...</p>
      </div>
    );
  }

  return (
    <div className="subject-management">
      {/* Header with Back Button */}
      <div className="management-header">
        <div className="header-left">
          <button className="back-btn" onClick={onBack}>
            ← Back to Dashboard
          </button>
          <h2>Subject Management</h2>
        </div>
        <button 
          className="add-btn"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? 'Cancel' : '+ Add Subject'}
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={() => setError('')}>×</button>
        </div>
      )}
      
      {success && (
        <div className="alert alert-success">
          {success}
          <button onClick={() => setSuccess('')}>×</button>
        </div>
      )}

      {/* Add/Edit Subject Form */}
      {showAddForm && (
        <div className="add-form-container">
          <h3>{editingSubject ? 'Edit Subject' : 'Add New Subject'}</h3>
          <form onSubmit={handleSubmit} className="add-form">
            <div className="form-group">
              <label>Subject Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., Mathematics, Physics, Chemistry"
                required
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description of the subject (optional)"
                rows="3"
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="submit-btn">
                {editingSubject ? 'Update Subject' : 'Create Subject'}
              </button>
              <button type="button" className="cancel-btn" onClick={cancelForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Subjects Grid */}
      <div className="subjects-container">
        <h3>All Subjects ({subjects.length})</h3>
        
        {subjects.length === 0 ? (
          <div className="no-data">
            <p>No subjects found. Add your first subject!</p>
          </div>
        ) : (
          <div className="subjects-grid">
            {subjects.map(subject => (
              <div key={subject.subject_id} className="subject-card">
                <div className="card-header">
                  <h4>{subject.name}</h4>
                  <div className="card-actions">
                    <button 
                      className="action-btn edit"
                      onClick={() => handleEdit(subject)}
                      title="Edit"
                    >
                      ✏️
                    </button>
                    <button 
                      className="action-btn delete"
                      onClick={() => handleDelete(subject.subject_id)}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                
                <div className="card-content">
                  <p className="subject-description">
                    {subject.description || 'No description provided'}
                  </p>
                  <div className="card-meta">
                    <span className="subject-id">ID: {subject.subject_id}</span>
                    <span className="created-date">
                      Created: {new Date(subject.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirm Delete Dialog */}
      {showConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <ConfirmDialog
              message="Are you sure you want to delete this subject? This action cannot be undone."
              onConfirm={confirmDelete}
              onCancel={cancelDelete}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SubjectManagement;